import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Box, Grid, Dialog, DialogContent, Button, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useTranslation } from 'react-i18next';
import { HousingType } from '../types';
import HousingRecapBox from '../components/HousingRecapBox';
import { HomeInsurancePaymentWrapper } from '../components/HomeInsurancePaymentForm';
import PaymentSelector from '../components/PaymentSelector';
import StartDateSelector from '../components/StartDateSelector';
import { updateStartDate } from '../habitationSlice';
import WarrantyAccordion from '../components/WarrantyAccordion';
import InfosPdf from '../components/InfosPdf';

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const isDateBeforeToday = (isoStr: string | null) => {
  if (!isoStr) return false;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return true;
  return d < startOfToday();
};

const Spacing = styled.div`
  height: 50px;
  @media (max-width: 600px) {
    height: 10px;
  }
`;

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  width: 150px;
  height: 35px;
  font-weight: 400;
  font-size: 15px;
  &:hover: {
    background-color: #26555f;
  }
  @media (max-width: 600px) {
      width: 100%;
      font-size: 13px;
      height: 30px;
  }
`;


const RecapAndSignatureStep = () => {
    const { t } = useTranslation('habitation');
    const [paymentModalOpen, setPaymentModalOpen] = useState(false);
    const dispatch = useAppDispatch();

    const { address, housingType, floorLevel, numberOfRooms, startDate, roommates, price, paymentType} = useAppSelector(state => state.habitation);
    const isRoom = housingType === HousingType.room;
    const isStartDateOutdated = isDateBeforeToday(startDate);

    const handleSubmitContract = () => {
        // Le bouton est désactivé quand la date est périmée, mais on revérifie au cas où.
        if (isDateBeforeToday(startDate)) return;
        setPaymentModalOpen(true);
    };

    return ( 
        <Box sx={{ maxWidth: 1000, mx: 'auto', p: 2 }}>
            <StartDateSelector
                 value={startDate}
                onChange={(newDate) => dispatch(updateStartDate(newDate))}
                isOutdated={isStartDateOutdated}
            />
            <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={7}>
                    {/* Recapitulatif */}
                    <HousingRecapBox isRoom={isRoom} floorLevel={floorLevel} address={address} roommates={roommates} numberOfRooms={numberOfRooms} housingType={housingType}/>
                    <InfosPdf viewDocuments={['warranty', 'ipid', 'cg']}/>
                </Grid>
                <Grid item xs={0} sm={1}>
                </Grid>
                <Grid item xs={12} sm={4} >
                    {/* Mode de paiement */}
                    <PaymentSelector
                        housingType={housingType === HousingType.room ? 'room' : 'full'}
                        numberOfRooms={numberOfRooms}
                        onChange={() => {
                            // Payment type selected
                        }}
                        onSubmit={handleSubmitContract}
                    />
                </Grid>
            </Grid>
            <Dialog open={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogContent>
                    {address && (floorLevel || floorLevel == 0) && (
                        <HomeInsurancePaymentWrapper
                            paymentType={paymentType} 
                            housingType={housingType === HousingType.room ? 'room' : 'full'}
                            numberOfRooms={numberOfRooms}
                            startDate={startDate}
                            address={address}
                            floorLevel={floorLevel}
                            roommates={roommates}
                        />
                    )}
                </DialogContent>
            </Dialog>
            <Spacing />
            <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={12}>
                    {/* Garanties */}
                    <WarrantyAccordion />
                    <InfosPdf viewDocuments={['warranty']}/>
                </Grid>
                 <Grid item xs={0} sm={9}>
                 </Grid>
                <Grid item xs={12} sm={3} justifyContent="right" alignItems="right" spacing={1} mt={0}>
                    <Grid container direction="column" alignItems="right" spacing={2}>
                        <Grid item xs={12}>
                            {price !== null && (
                            <Typography fontSize={{xs: '18px', sm: '28px'}} fontWeight={400}>
                            {price.toFixed(2).replace('.', ',')}€
                            </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                        <ButtonStyled
                            disabled={!startDate || isStartDateOutdated}
                            onClick={handleSubmitContract}
                            variant="contained"
                        >
                            {t('step5.subscribe')}
                        </ButtonStyled>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
};

export default RecapAndSignatureStep;